// apps/client/src/AcceptInvitationPage.tsx
/**
 * JEFF DEAN GOOGLE ENTERPRISE PATTERN: Simplified Invitation Acceptance
 *
 * This component implements the client-side of the atomic invitation acceptance flow.
 * All complexity has been moved to the backend createAccountAndAcceptInvitation function.
 *
 * Flow:
 * 1. User fills single form with: email (readonly), password, confirm password, display name
 * 2. Client calls createAccountAndAcceptInvitation backend function
 * 3. Backend atomically: validates invitation, creates auth account, creates profile, sets claims
 * 4. Backend returns custom token
 * 5. Client signs in with custom token
 * 6. AuthContext detects authenticated user and navigates to dashboard
 *
 * Zero race conditions. ~200 lines instead of 925.
 *
 * @see docs/handovers/invitation-acceptance-flow-fix-needed.md
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  signInWithCustomToken,
  signInWithPopup,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  getAdditionalUserInfo,
} from 'firebase/auth';
import { auth, functions } from './lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from './hooks/useAuth';
import { Eye, EyeOff, Lock, Mail, AlertCircle, Shield, Clock, User, CheckCircle, LucideProps } from 'lucide-react';
import { Spinner } from './components/common/Spinner';
import MfaEnrollmentCard from './components/auth/MfaEnrollmentCard';
import MfaChallengeCard from './components/auth/MfaChallengeCard';
import { isMfaRequiredError, buildMfaResolver } from './lib/nativeMfa';
import type { MultiFactorResolver } from 'firebase/auth';

// React 19 compatibility: Cast icons to satisfy JSX requirements
// See: https://github.com/lucide-icons/lucide/issues/1482
type IconProps = Omit<LucideProps, 'ref'>;
const AlertCircleIcon = AlertCircle as unknown as React.FC<IconProps>;
const ClockIcon = Clock as unknown as React.FC<IconProps>;
const ShieldIcon = Shield as unknown as React.FC<IconProps>;
const EyeIcon = Eye as unknown as React.FC<IconProps>;
const EyeOffIcon = EyeOff as unknown as React.FC<IconProps>;
const MailIcon = Mail as unknown as React.FC<IconProps>;
const LockIcon = Lock as unknown as React.FC<IconProps>;
const UserIcon = User as unknown as React.FC<IconProps>;
const CheckCircleIcon = CheckCircle as unknown as React.FC<IconProps>;

interface InvitationData {
  id: string;
  token: string;
  email: string;
  role: string;
  companyId: string;
  companyName: string;
  officeId?: string;
  invitedBy: string;
  status: 'pending' | 'accepted' | 'expired';
  expiresAt: Date;
}

// ============================================================================
// PASSWORD VALIDATION UTILITIES
// ============================================================================

const validatePasswordRequirements = (password: string) => {
  const requirements = {
    length: {
      met: password.length >= 12,
      message: 'At least 12 characters'
    },
    uppercase: {
      met: /[A-Z]/.test(password),
      message: 'One uppercase letter'
    },
    lowercase: {
      met: /[a-z]/.test(password),
      message: 'One lowercase letter'
    },
    number: {
      met: /\d/.test(password),
      message: 'One number'
    },
    special: {
      met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      message: 'One special character'
    }
  };

  const allMet = Object.values(requirements).every(req => req.met);

  return {
    requirements,
    allMet,
    score: Object.values(requirements).filter(req => req.met).length
  };
};

const calculatePasswordStrength = (password: string) => {
  if (!password) return { label: '', percentage: 0, color: '' };

  const validation = validatePasswordRequirements(password);
  const percentage = (validation.score / 5) * 100;

  if (percentage === 100) return { label: 'Excellent', percentage, color: 'from-green-500 to-emerald-600' };
  if (percentage >= 80) return { label: 'Strong', percentage, color: 'from-green-400 to-green-500' };
  if (percentage >= 60) return { label: 'Good', percentage, color: 'from-yellow-500 to-green-400' };
  if (percentage >= 40) return { label: 'Fair', percentage, color: 'from-orange-500 to-yellow-500' };
  return { label: 'Weak', percentage, color: 'from-red-500 to-red-600' };
};

// ============================================================================
// ERROR MESSAGE TRANSLATOR
// ============================================================================

const getErrorMessage = (err: any): string => {
  const errorCode = err?.code || '';

  switch (errorCode) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Incorrect password';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support';
    case 'auth/invalid-email':
      return 'Invalid email address';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection';
    case 'auth/weak-password':
      return 'Password must be at least 12 characters';
    case 'functions/invalid-argument':
      return err.message || 'Invalid input. Please check all fields';
    case 'functions/not-found':
      return 'Invitation not found or has been used';
    case 'functions/permission-denied':
      return 'Invalid invitation token';
    case 'functions/failed-precondition':
      return err.message || 'Invitation has already been used';
    case 'functions/deadline-exceeded':
      return 'Invitation has expired';
    case 'auth/internal-error':
      // WS8b: a sign-in refused by the MFA blocking function surfaces to the
      // Web SDK as auth/internal-error with no usable code. On THIS page the
      // only account that reaches it is an existing, already-privileged one
      // whose workspace enforces two-factor and which holds no factor, so say
      // so instead of showing the raw platform string.
      // NOTE: depends on WS8a D6 for the shared classifier in lib/mfaErrors.ts;
      // this local branch covers the invitation surface until that lands.
      return 'This account already has access to a workspace that requires two-factor authentication, and no authenticator is set up on it. Sign in on the login page and follow the two-factor prompt, or ask your administrator to reset it.';
    default:
      return err.message || 'An error occurred. Please try again';
  }
};

// ============================================================================
// F1 (2026-08-22): "this invitation is finished", and the TWO ways we learn it
// ============================================================================
// Live incident, 20 Aug: an invitation that had already set the account up was
// still rendered as "Continue your setup", with a sign-in form that could only
// fail. The page could not tell, because `validateInvitation` refuses every
// non-pending invitation with one undifferentiated `failed-precondition`.
//
//   1. `invitation_already_accepted`: the mount-time validate now says so
//      explicitly, and ONLY for a consumed invitation: the server excludes the
//      unfinished-post-steps shape and the failed-revoke tail, both of which
//      must keep offering "Finish setup".
//   2. `invitation_no_pending_for_account`: the stale-cache case. An earlier
//      validate returned `enrolmentInProgress`, the page is sitting on the
//      resume screen, the invitation has since been consumed, and the resume
//      call finds nothing to resume.
//
// The message fallback covers a page held open across the deploy that adds the
// codes; it matches the server's own sentence and nothing else.
const CONSUMED_INVITATION_ERROR_CODES = [
  'invitation_already_accepted',
  'invitation_no_pending_for_account',
];

// F2 (G-02, 2026-08-22): the park branches no longer return a `customToken`.
// It was a roleless credential nobody consumed: since the 2026-08-20 P0 fix the
// enrolment card runs on the invitee's OWN first-factor session, because
// Identity Platform refuses `multiFactor().getSession()` on a `customAuth`
// first factor. The field is therefore optional on the wire, present only on
// the inline (off/observe) branch, which is the only branch that signs in with
// it. Every use is funnelled through here so an absent token is a stated
// failure rather than `signInWithCustomToken(auth, undefined)`.
const requireInlineSignInToken = (token: string | undefined): string => {
  if (!token) {
    throw new Error(
      'The server did not return a sign-in token for this invitation. Please try again.',
    );
  }
  return token;
};

const isConsumedInvitationError = (err: any): boolean => {
  const detailCode = err?.details?.code;
  if (typeof detailCode === 'string' && CONSUMED_INVITATION_ERROR_CODES.includes(detailCode)) {
    return true;
  }
  return /no pending invitation found/i.test(String(err?.message || ''));
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AcceptInvitationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Form state
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  // UI state
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [googleSignInActive, setGoogleSignInActive] = useState<boolean>(false);

  // ========================================================================
  // WS8b - enrolment BEFORE the claim grant (plan-v5 §7 Phase 6b, ruling 3)
  // ========================================================================
  // When the tenant's MFA mode is `enforce`, the bootstrap callable returns a
  // ROLELESS custom token and grants nothing. We sign in with it, render the
  // enrolment card RIGHT HERE on the public route (a claim-less session can
  // never enter the protected tree - AuthContext requires claims.companyId),
  // and only then call `finaliseInvitationEnrolment`, which asserts the factor
  // and grants the claims. `null` for every tenant at off/observe, i.e. every
  // live tenant today, where the flow below is untouched.
  const [enrolmentInvitationId, setEnrolmentInvitationId] = useState<string | null>(null);
  const [enrolmentFinalised, setEnrolmentFinalised] = useState<boolean>(false);
  const [finalising, setFinalising] = useState<boolean>(false);
  const [enrolmentError, setEnrolmentError] = useState<string>('');

  // WS8b F2 (V-04): once an invitation is PARKED awaiting enrolment, the raw
  // link stops being sufficient. Only the account that parked it may continue,
  // so the invitee resumes by signing in with the password they chose the first
  // time. `resumeRequired` switches the form into that mode.
  const [resumeRequired, setResumeRequired] = useState<boolean>(false);
  const [resuming, setResuming] = useState<boolean>(false);
  // R4 (V2-04): the resume sign-in is CHALLENGED once the invitee has enrolled
  // (that is the whole point of the window they abandoned in), so the resume
  // screen needs the same live-resolver challenge the login page uses.
  const [resumeMfaResolver, setResumeMfaResolver] = useState<MultiFactorResolver | null>(null);
  // F-C (V3-03): which leg parked this invitation. A Google-bootstrapped
  // account has no password, so offering it the password resume form was a
  // confident route into something that cannot work, with copy telling the user
  // to use "the password you chose the first time" that they never chose.
  const [resumeFlow, setResumeFlow] = useState<string | null>(null);
  // F-E (V3-06): finalise wrote the claims but could not kill the bootstrap
  // session. The obligation is durable server-side, but the only thing that
  // discharges it is this user retrying, so it has to be signposted.
  const [revocationRetry, setRevocationRetry] = useState<boolean>(false);
  // M10 (Codex SEC-8B-05): the RELOAD route back into a half-finished setup.
  // `validateInvitation` is public and rejects every `accepted` invitation as
  // already used, which is correct and is not changed here; but the bound owner
  // whose post steps failed then had no route at all once they reloaded, and
  // their live tab was the only thing keeping the repair reachable. When a
  // signed-in account is present, the already-accepted screen offers to finish
  // the setup, and the SERVER decides whether that is allowed.
  const [resumeFinalising, setResumeFinalising] = useState<boolean>(false);
  const [resumeError, setResumeError] = useState<string>('');
  const [resumeUnavailable, setResumeUnavailable] = useState<boolean>(false);
  // F1 (2026-08-22): this invitation is FINISHED. Terminal, and it outranks
  // every resume screen below, because the live incident of 20 Aug was a
  // consumed invitation still headed "Continue your setup" with a form that
  // could not succeed. See `markInvitationConsumed`.
  const [invitationConsumed, setInvitationConsumed] = useState<boolean>(false);

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState<any>(null);
  const [passwordStrength, setPasswordStrength] = useState<any>(null);

  const { user, isAuthenticated, logout, setInvitationAcceptanceMode, recheckProfile } = useAuth();

  /**
   * F1 (2026-08-22): a refusal that means "this invitation is finished" ends
   * the flow rather than decorating the screen it arrived on.
   *
   * Returns true when it handled the error, so each caller can return instead
   * of falling through to `setError`. Every resume surface is torn down in the
   * same breath, because the one thing the incident proved is that leaving a
   * resume affordance on screen next to a terminal message is worse than
   * either alone: the user keeps pressing it.
   */
  const markConsumedIfFinished = (err: any): boolean => {
    if (!isConsumedInvitationError(err)) return false;
    setInvitationConsumed(true);
    setResumeRequired(false);
    setResumeMfaResolver(null);
    setError('');
    setResumeError('');
    return true;
  };

  // ========================================================================
  // EFFECT: Load and Validate Invitation
  // ========================================================================

  useEffect(() => {
    const loadInvitation = async () => {
      const token = searchParams.get('token');
      const invitationId = searchParams.get('invitation');

      if (!token || !invitationId) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const validateInvitation = httpsCallable<{token: string, invitationId: string}, any>(
          functions,
          'validateInvitation'
        );
        const result = await validateInvitation({ token, invitationId });

        const invitationData = result.data;

        setInvitation({
          id: invitationId,
          token,
          ...invitationData,
          expiresAt: new Date(invitationData.expiresAt)
        });

        // WS8b R2 (V2-01): the server says this invitation is PARKED, i.e. the
        // account already exists and is part-way through enrolment. Go straight
        // to the resume screen: re-showing the create-account form would only
        // produce `invitation_enrolment_in_progress` from the callable, and
        // before R2 this callable rejected outright, which made one page
        // refresh a permanent lockout.
        if (invitationData?.enrolmentInProgress === true) {
          setResumeRequired(true);
          setResumeFlow(invitationData?.enrolmentBootstrapFlow ?? null);
        }

      } catch (err: any) {
        // F1: a CONSUMED invitation gets the terminal card, not the generic
        // "already used" screen with its Finish setup affordance. The server
        // only sends this code when there is genuinely nothing left to finish.
        if (isConsumedInvitationError(err)) {
          setInvitationConsumed(true);
          setResumeRequired(false);
          setError('');
        } else {
          setError(getErrorMessage(err));
        }
      } finally {
        setLoading(false);
      }
    };

    loadInvitation();
  }, [searchParams]);

  // ========================================================================
  // EFFECT: Auto-navigate when authenticated
  // ========================================================================

  useEffect(() => {
    if (isAuthenticated && showSuccess) {
      console.log('[AcceptInvitation] User authenticated, navigating to dashboard');
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, showSuccess, navigate]);

  // ========================================================================
  // EFFECT: Update password validation
  // ========================================================================

  useEffect(() => {
    if (password) {
      const validation = validatePasswordRequirements(password);
      const strength = calculatePasswordStrength(password);
      setPasswordValidation(validation);
      setPasswordStrength(strength);
    } else {
      setPasswordValidation(null);
      setPasswordStrength(null);
    }
  }, [password]);

  // ========================================================================
  // HANDLER: Form Submission (THE ENTIRE FLOW IN ONE CALL)
  // ========================================================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation) return;

    // Client-side validation
    if (!passwordValidation?.allMet) {
      setError('Password does not meet security requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!displayName.trim() || displayName.trim().length < 2) {
      setError('Please enter your full name (at least 2 characters)');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      console.log('[AcceptInvitation] Calling createAccountAndAcceptInvitation...');

      // ====================================================================
      // ATOMIC BACKEND CALL: Everything happens here
      // ====================================================================

      const createAccountAndAcceptInvitation = httpsCallable<
        {
          email: string;
          password: string;
          displayName: string;
          token: string;
          invitationId: string;
        },
        {
          success: boolean;
          /** F2: inline (off/observe) branch ONLY; absent on the park branch. */
          customToken?: string;
          message: string;
          companyName?: string;
          role?: string;
          /** WS8b: present ONLY when the tenant's MFA mode is `enforce`. */
          requiresEnrolment?: boolean;
          tenantMfaMode?: 'off' | 'observe' | 'enforce';
          invitationId?: string;
          uid?: string;
        }
      >(functions, 'createAccountAndAcceptInvitation');

      const result = await createAccountAndAcceptInvitation({
        email: invitation.email,
        password,
        displayName: displayName.trim(),
        token: invitation.token,
        invitationId: invitation.id,
      });

      console.log('[AcceptInvitation] Backend returned success:', result.data.message);

      // ====================================================================
      // WS8b: an ENFORCING tenant returns a ROLELESS token and no claims.
      // Hold AuthContext in invitation-acceptance mode across the sign-in so
      // it does not resolve a profile for the claim-less session, then render
      // the enrolment card. Nothing here runs for a tenant at off/observe.
      // ====================================================================
      const needsEnrolment = result.data.requiresEnrolment === true;
      if (needsEnrolment) {
        setInvitationAcceptanceMode(true);

        // ==================================================================
        // 2026-08-20 P0: enrol on the FIRST-FACTOR session, never the token
        // ==================================================================
        // Identity Platform refuses MFA enrolment on a `customAuth` first
        // factor: `multiFactor(user).getSession()` throws
        // `auth/unsupported-first-factor`, which blocked EVERY invitee of an
        // enforcing tenant at the QR step. The password the invitee just chose
        // was applied by the server on this same call, so signing in with it
        // produces a supported first factor (`password`) on the very account
        // that parked the invitation. F2 (2026-08-22): the server no longer
        // returns a `customToken` on this branch at all.
        console.log('[AcceptInvitation] Tenant enforces MFA - enrolling on the password session');
        await signInWithEmailAndPassword(auth, invitation.email, password);
        setEnrolmentInvitationId(result.data.invitationId || invitation.id);
        return;
      }

      // ====================================================================
      // Sign in with custom token (already has all claims set). Unchanged for
      // every tenant at off/observe.
      // ====================================================================

      console.log('[AcceptInvitation] Signing in with custom token...');
      await signInWithCustomToken(auth, requireInlineSignInToken(result.data.customToken));

      console.log('[AcceptInvitation] Sign-in successful, showing success screen');

      // Show success screen
      setShowSuccess(true);

      // AuthContext will detect the authenticated user and isAuthenticated will become true
      // The useEffect above will then navigate to dashboard

    } catch (err: any) {
      console.error('[AcceptInvitation] Error:', err);
      if (isConsumedInvitationError(err)) {
        // F1: nothing left to accept. Terminal card, never the resume form.
        setInvitationConsumed(true);
        setResumeRequired(false);
        setError('');
      } else if (err?.details?.code === 'invitation_enrolment_in_progress') {
        // WS8b F2: this invitation is already part-way through setup. The raw
        // link cannot start it again; the invitee signs in and resumes.
        setResumeRequired(true);
        setError('');
      } else {
        setError(getErrorMessage(err));
      }
      setShowSuccess(false);
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================================================
  // WS8b F2 HANDLER: resume a parked setup by signing in
  // ========================================================================
  // A roleless first-factor sign-in is permitted by the blocking function's
  // roleless_bootstrap branch, so the invitee can always get back in with the
  // password they chose. Once signed in as the account that parked the
  // invitation, the accept callable is re-called AUTHENTICATED and the server's
  // uid binding lets it through.

  /**
   * R4: everything after a successful (possibly challenged) resume sign-in.
   * Split out so the plain path and the MFA-challenge path share it exactly.
   */
  const continueResumeAfterSignIn = async (): Promise<void> => {
    if (!invitation) return;
    const createAccountAndAcceptInvitation = httpsCallable<
      {
        email: string;
        password: string;
        displayName: string;
        token: string;
        invitationId: string;
      },
      {
        success: boolean;
        /** F2: inline (off/observe) branch ONLY; absent on the park branch. */
        customToken?: string;
        message: string;
        requiresEnrolment?: boolean;
        invitationId?: string;
      }
    >(functions, 'createAccountAndAcceptInvitation');

    const result = await createAccountAndAcceptInvitation({
      email: invitation.email,
      password,
      displayName: displayName.trim() || invitation.email.split('@')[0],
      token: invitation.token,
      invitationId: invitation.id,
    });

    // 2026-08-20 P0: the resume already signed in with email + password, so the
    // live session IS a supported first factor. Keep it and enrol on it; a
    // custom-token session cannot enrol (`auth/unsupported-first-factor`).
    if (result.data.requiresEnrolment === true) {
      setResumeRequired(false);
      setEnrolmentInvitationId(result.data.invitationId || invitation.id);
      return;
    }

    await signInWithCustomToken(auth, requireInlineSignInToken(result.data.customToken));

    setInvitationAcceptanceMode(false);
    setShowSuccess(true);
  };

  const handleResumeSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation || resuming) return;
    setResuming(true);
    setError('');
    try {
      setInvitationAcceptanceMode(true);
      await signInWithEmailAndPassword(auth, invitation.email, password);
      await continueResumeAfterSignIn();
    } catch (err: any) {
      // R4 (V2-04): an invitee who enrolled but did not finalise HAS a factor,
      // so their resume sign-in is challenged. Without this branch the window
      // between enrolling and finalising, which is exactly when people wander
      // off to install an authenticator app, was an unrecoverable lockout:
      // signing in through the login page does not help either, because they
      // still hold no claims and cannot reach the finalise callable from
      // anywhere in the protected tree.
      if (isMfaRequiredError(err)) {
        setResumeMfaResolver(buildMfaResolver(auth, err));
        setResuming(false);
        return;
      }
      console.error('[AcceptInvitation] Resume failed:', err);
      setInvitationAcceptanceMode(false);
      await auth.signOut().catch(() => {});
      if (markConsumedIfFinished(err)) return;
      setError(getErrorMessage(err));
    } finally {
      setResuming(false);
    }
  };

  /** R4: the challenge resolved, so the session is live. Finish the resume. */
  const handleResumeChallengeSuccess = async (): Promise<void> => {
    setResumeMfaResolver(null);
    setResuming(true);
    setError('');
    try {
      // F-C: continue via the leg that parked this invitation.
      if (resumeFlow === 'acceptInvitation') {
        await continueResumeViaAcceptInvitation();
      } else {
        await continueResumeAfterSignIn();
      }
    } catch (err: any) {
      console.error('[AcceptInvitation] Resume after challenge failed:', err);
      setInvitationAcceptanceMode(false);
      await auth.signOut().catch(() => {});
      if (markConsumedIfFinished(err)) return;
      setError(getErrorMessage(err));
    } finally {
      setResuming(false);
    }
  };

  // ========================================================================
  // HANDLER: Google Sign-In (Uses original acceptInvitation flow)
  // ========================================================================

  /**
   * F-C (V3-03): resume a Google-bootstrapped invitation.
   *
   * Re-runs the popup as the parked uid and then re-calls `acceptInvitation`
   * AUTHENTICATED, which is the call R3 widened to find its own parked
   * invitation. Without this the server-side resume was unreachable and the
   * Google invitee was routed into a password form for an account that has no
   * password. An MFA challenge on the popup lands in the same challenge card as
   * the password resume, because an invitee who enrolled but did not finalise
   * is challenged on every provider.
   */
  const handleResumeGoogleSignIn = async (): Promise<void> => {
    if (!invitation || resuming) return;
    setResuming(true);
    setError('');
    try {
      setInvitationAcceptanceMode(true);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ login_hint: invitation.email });
      provider.addScope('profile');
      provider.addScope('email');
      const credential = await signInWithPopup(auth, provider);

      if (credential.user.email?.toLowerCase() !== invitation.email.toLowerCase()) {
        await auth.signOut();
        throw new Error('Please sign in with the email address that received the invitation');
      }

      await continueResumeViaAcceptInvitation();
    } catch (err: any) {
      const errorCode = err?.code || '';
      if (errorCode === 'auth/popup-closed-by-user' || errorCode === 'auth/cancelled-popup-request') {
        setInvitationAcceptanceMode(false);
        setResuming(false);
        return;
      }
      if (isMfaRequiredError(err)) {
        setResumeMfaResolver(buildMfaResolver(auth, err));
        setResuming(false);
        return;
      }
      console.error('[AcceptInvitation] Google resume failed:', err);
      setInvitationAcceptanceMode(false);
      await auth.signOut().catch(() => {});
      if (markConsumedIfFinished(err)) return;
      setError(getErrorMessage(err));
    } finally {
      setResuming(false);
    }
  };

  /** F-C: the authenticated accept-resume for the Google leg. */
  const continueResumeViaAcceptInvitation = async (): Promise<void> => {
    if (!invitation) return;
    const acceptInvitationFn = httpsCallable<
      { displayName: string },
      {
        success: boolean;
        message?: string;
        /** F2: inline (off/observe) branch ONLY; absent on the park branch. */
        customToken?: string;
        requiresEnrolment?: boolean;
        invitationId?: string;
      }
    >(functions, 'acceptInvitation');

    const result = await acceptInvitationFn({
      displayName: auth.currentUser?.displayName || invitation.email.split('@')[0],
    });

    // 2026-08-20 P0: the Google popup session is a supported first factor
    // (`google.com`). Enrol on it. Signing it out and signing back in with the
    // roleless custom token made the enrolment card unusable for every invitee
    // of an enforcing tenant (`auth/unsupported-first-factor`).
    if (result.data.requiresEnrolment === true) {
      setResumeRequired(false);
      setEnrolmentInvitationId(result.data.invitationId || invitation.id);
      return;
    }

    await auth.signOut();
    await signInWithCustomToken(auth, requireInlineSignInToken(result.data.customToken));

    setInvitationAcceptanceMode(false);
    setShowSuccess(true);
  };

  const handleGoogleSignIn = async () => {
    if (!invitation) return;

    setError('');
    setGoogleSignInActive(true);

    // CRITICAL: Enable invitation acceptance mode BEFORE sign-in
    // This suppresses AuthContext's onAuthStateChanged profile check
    // while the profileless Google session exists
    setInvitationAcceptanceMode(true);

    try {
      // 1. Google popup sign-in
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ login_hint: invitation.email });
      provider.addScope('profile');
      provider.addScope('email');

      const userCredential = await signInWithPopup(auth, provider);
      const additionalUserInfo = getAdditionalUserInfo(userCredential);

      console.log('[AcceptInvitation] Google sign-in successful:', {
        displayName: userCredential.user.displayName,
        email: userCredential.user.email,
        isNewUser: additionalUserInfo?.isNewUser,
      });

      // 2. Verify email matches invitation
      if (userCredential.user.email?.toLowerCase() !== invitation.email.toLowerCase()) {
        await auth.signOut();
        throw new Error('Please sign in with the email address that received the invitation');
      }

      // 3. Extract display name from Google profile
      const profileName = typeof additionalUserInfo?.profile?.name === 'string'
        ? additionalUserInfo.profile.name
        : null;
      const googleDisplayName = userCredential.user.displayName ||
                               profileName ||
                               userCredential.user.email?.split('@')[0] ||
                               'Unknown User';

      // 4. Backend creates profile + sets claims + returns custom token (atomic)
      const acceptInvitationFn = httpsCallable<
        { displayName: string },
        {
          success: boolean;
          message?: string;
          /** F2: inline (off/observe) branch ONLY; absent on the park branch. */
          customToken?: string;
          /** WS8b: present ONLY when the tenant's MFA mode is `enforce`. */
          requiresEnrolment?: boolean;
          tenantMfaMode?: 'off' | 'observe' | 'enforce';
          invitationId?: string;
          uid?: string;
        }
      >(functions, 'acceptInvitation');

      const result = await acceptInvitationFn({ displayName: googleDisplayName });

      if (!result.data.success) {
        throw new Error(result.data.message || 'Failed to accept invitation');
      }

      console.log('[AcceptInvitation] Invitation accepted successfully via Google');

      // WS8b: an ENFORCING tenant granted NOTHING on this call.
      //       Invitation-acceptance mode must stay ON across the sign-in below,
      //       so AuthContext does not resolve a profile for the claim-less
      //       session and bounce it.
      const needsEnrolment = result.data.requiresEnrolment === true;

      // 4b. 2026-08-20 P0: under enforce the enrolment happens on THIS session.
      //    The Google popup session is a supported first factor (`google.com`);
      //    the roleless custom token this branch used to receive was a
      //    `customAuth` one, and Identity Platform refuses to open a
      //    multi-factor session on it, which is what blocked every invitee of
      //    an enforcing tenant at the QR step. So the session is kept,
      //    invitation-acceptance mode stays ON for the whole parked session,
      //    and the claims arrive by token refresh after finalise. F2
      //    (2026-08-22): the server no longer mints that token at all on the
      //    park branch. Steps 5 to 7 below are the unchanged off/observe path,
      //    and the ONLY branch that still signs in with a custom token.
      if (needsEnrolment) {
        console.log('[AcceptInvitation] Tenant enforces MFA - enrolling on the Google session');
        setEnrolmentInvitationId(result.data.invitationId || invitation.id);
        setGoogleSignInActive(false);
        return;
      }

      // 5. Sign out the profileless Google session
      //    onAuthStateChanged(null) fires here — suppressed because ref is still true
      await auth.signOut();

      // 6. Disable invitation mode AFTER signOut (null callback already suppressed)
      //    but BEFORE signInWithCustomToken (so full profile check runs).
      setInvitationAcceptanceMode(false);

      // 7. Sign in atomically — profile and claims already exist
      await signInWithCustomToken(auth, requireInlineSignInToken(result.data.customToken));

      // 8. onAuthStateChanged has now run the full profile check
      //    isAuthenticated=true, useEffect will navigate to dashboard
      setShowSuccess(true);

    } catch (err: any) {
      const errorCode = err?.code || '';

      // Don't show error for user-cancelled actions
      if (errorCode === 'auth/popup-closed-by-user' || errorCode === 'auth/cancelled-popup-request') {
        setGoogleSignInActive(false);
        setInvitationAcceptanceMode(false);
        return;
      }

      if (err?.details?.code === 'invitation_enrolment_in_progress') {
        // WS8b F2: parked by another session; resume via password sign-in.
        setResumeRequired(true);
        setError('');
      } else {
        setError(getErrorMessage(err));
      }
      setInvitationAcceptanceMode(false);
      await auth.signOut().catch(() => {});
    } finally {
      setGoogleSignInActive(false);
    }
  };

  // ========================================================================
  // WS8b HANDLER: finalise - grant the claims now that a factor is enrolled
  // ========================================================================

  const handleFinaliseEnrolment = async (): Promise<void> => {
    if (!enrolmentInvitationId || finalising) return;
    setFinalising(true);
    setEnrolmentError('');
    try {
      const finaliseInvitationEnrolment = httpsCallable<
        { invitationId: string },
        { success: boolean; message?: string; granted?: boolean }
      >(functions, 'finaliseInvitationEnrolment');

      await finaliseInvitationEnrolment({ invitationId: enrolmentInvitationId });

      // ==================================================================
      // 2026-08-20 P0 (E2): collect the claims on THIS session
      // ==================================================================
      // The enrolment session is now the invitee's own first factor, not a
      // throwaway custom-token one, so the claims the grant just wrote are
      // picked up by refreshing it rather than by signing it out.
      //
      // HONEST LIMIT: the server still revokes this uid's refresh tokens on the
      // same call (`invitationGrantShared.ts` line 1228, plan-v5 §3: the next
      // sign-in must be a real, challenged one), so today this refresh is
      // EXPECTED to fail with auth/user-token-expired and the fallback below is
      // the live path. It is not an error, and it is not a dead end: the
      // invitee is told plainly to sign in again, now with their authenticator.
      // Retiring that revoke is a recorded server follow-up; when it lands, the
      // refresh succeeds and this page walks straight into the workspace.
      let claimsRefreshed = false;
      try {
        await auth.currentUser?.getIdToken(true);
        claimsRefreshed = !!auth.currentUser;
      } catch {
        claimsRefreshed = false;
      }

      if (claimsRefreshed) {
        setInvitationAcceptanceMode(false);
        await recheckProfile();
        // Leave the enrolment stage: its render branch sits above the success
        // screen, so it would otherwise keep the card on top of a granted
        // session.
        setEnrolmentInvitationId(null);
        setShowSuccess(true);
        return;
      }

      setInvitationAcceptanceMode(false);
      await auth.signOut().catch(() => {});
      setEnrolmentFinalised(true);
    } catch (err: any) {
      const detailCode = err?.details?.code;
      if (detailCode === 'revocation_pending_retry') {
        // F-E (V3-06): the claims ARE written; only the bootstrap-session
        // revoke failed, and the obligation is durable server-side. Retrying
        // finalise is what discharges it, so say so and offer the button
        // rather than showing a generic error the user will walk away from.
        setRevocationRetry(true);
        setEnrolmentError('');
      } else if (detailCode === 'mfa_enrolment_required_before_grant') {
        // The server re-read the factors and found none: stay on the card.
        setEnrolmentError(
          'Two-factor authentication is not switched on yet. Finish the setup below to activate your access.',
        );
      } else {
        setEnrolmentError(getErrorMessage(err));
      }
    } finally {
      setFinalising(false);
    }
  };

  // ========================================================================
  // WS8b M10 HANDLER: finish a half-completed setup after a reload
  // ========================================================================
  // Same callable, same payload as the in-page Continue. The server is the
  // authority: it re-asserts ownership, the enrolled factor and the tenant, and
  // refuses anything that is not this invitation's bound owner. A refusal is
  // therefore not an error to argue with, it means "this is not your repair",
  // and the screen falls back to the ordinary sign-in copy.
  const handleResumeFinishSetup = async (): Promise<void> => {
    const invitationId = searchParams.get('invitation');
    if (!invitationId || resumeFinalising) return;
    setResumeFinalising(true);
    setResumeError('');
    try {
      const finaliseInvitationEnrolment = httpsCallable<
        { invitationId: string },
        { success: boolean; message?: string; granted?: boolean }
      >(functions, 'finaliseInvitationEnrolment');

      await finaliseInvitationEnrolment({ invitationId });

      // The grant revokes the bootstrap session, so this refresh is expected to
      // fail; that is the success path, exactly as in handleFinaliseEnrolment.
      try {
        await auth.currentUser?.getIdToken(true);
      } catch {
        // Expected: the session that performed the repair was revoked.
      }
      setInvitationAcceptanceMode(false);
      await auth.signOut().catch(() => {});
      setEnrolmentFinalised(true);
    } catch (err: any) {
      const code = err?.code;
      const detailCode = err?.details?.code;

      // P5 (V7-05): `failed-precondition` is not one answer, it is two, and
      // they need opposite treatments. `mfa_enrolment_required_before_grant` is
      // the ONE refusal on this path the invitee can actually act on: it means
      // "you are the right person, your workspace requires a second factor, and
      // you do not currently have one". Hiding it left them on a dead end with
      // the screen saying nothing at all, on exactly the path M10 exists to
      // open. Surface the server's message and KEEP the action, so they can
      // enrol and press it again.
      if (detailCode === 'mfa_enrolment_required_before_grant') {
        setResumeError(getErrorMessage(err));
        setResumeUnavailable(false);
      } else if (detailCode === 'invitation_setup_ended') {
        // R3 (Codex SEC-8B-10): an administrator ended this setup session. The
        // invitee cannot act on it, so the action goes, but staying silent here
        // would leave them pressing a button that vanished for no stated
        // reason. Show the server's sentence and remove the action.
        setResumeError(getErrorMessage(err));
        setResumeUnavailable(true);
      } else if (
        code === 'functions/permission-denied' ||
        code === 'permission-denied' ||
        detailCode === 'invitation_finalise_denied' ||
        detailCode === 'invitation_no_longer_active' ||
        code === 'functions/failed-precondition' ||
        code === 'failed-precondition'
      ) {
        // Not this account's setup to finish, or nothing left to finish. Hide
        // the action and leave the existing "sign in" route as the answer.
        setResumeUnavailable(true);
        setResumeError('');
      } else {
        setResumeError(getErrorMessage(err));
      }
    } finally {
      setResumeFinalising(false);
    }
  };

  // ========================================================================
  // R2 (Codex SEC-8B-05): the resume action, reachable in the REAL reload state
  // ========================================================================
  // The state this repair exists for is an invitation already flipped to
  // `accepted` whose post steps did not finish. `validateInvitation` refuses an
  // accepted invitation outright (`failed-precondition`, "Invitation already
  // used."), so the page never receives that invitation's status, its email, its
  // `setupUnfinished` marker or its `postStepsDone` marker: this error IS the
  // accepted signal, and it is the whole of what the page can read. See the
  // round-8 report for why the callable was left unchanged.
  //
  // That is sufficient because the SERVER is the authority. finalise re-asserts
  // ownership against `enrolmentBootstrapUid`, the enrolled factor, the tenant
  // and the administrator stop marker, and answers anyone else with a uniform
  // `permission-denied` that carries no status, tenant or existence signal. A
  // wrong guess here therefore costs one refused call and hides the action.
  //
  // Rendered on BOTH screens. The authenticated reload hits "Already Logged In"
  // first (that branch returns before the accepted screen), which is exactly why
  // the affordance was unreachable in production while its test passed: the test
  // hard-coded `isAuthenticated: false` in a state where AuthContext makes it
  // true.
  const invitationAlreadyUsed =
    !invitation && !!error && error.toLowerCase().includes('already used');
  const canOfferResumeFinish =
    Boolean(auth.currentUser) && invitationAlreadyUsed && !resumeUnavailable;

  const resumeFinishAction = canOfferResumeFinish ? (
    <div className="mb-4">
      <p className="mb-3 text-sm text-gray-400">
        If your setup did not finish, you can complete it now with the account
        you are signed in as.
      </p>
      <button
        onClick={handleResumeFinishSetup}
        disabled={resumeFinalising}
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {resumeFinalising ? 'Finishing setup...' : 'Finish setup'}
      </button>
      {resumeError && <p className="mt-3 text-sm text-red-300">{resumeError}</p>}
    </div>
  ) : invitationAlreadyUsed && resumeError ? (
    // R3: a refusal the invitee cannot act on (an administrator ended the
    // session) removes the action but must still SAY so. The silent refusals
    // clear `resumeError`, so they render nothing here, exactly as before.
    <p className="mb-4 text-sm text-red-300">{resumeError}</p>
  ) : null;

  // ========================================================================
  // RENDER: Loading State
  // ========================================================================

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="text-center">
          <Spinner size="lg" label="Loading invitation..." />
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: WS8b - enrolment finalised, sign in again (challenged)
  // ========================================================================
  // The grant revoked the bootstrap session on purpose, so there is no
  // dashboard to navigate into from here: the next sign-in is the first
  // challenged one. Say that plainly rather than bouncing to a dead session.

  if (enrolmentFinalised) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
            <CheckCircleIcon size={30} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">You&rsquo;re all set</h2>
          <p className="text-gray-400 text-sm mb-6 leading-relaxed">
            Two-factor authentication is on and your access to{' '}
            {invitation?.companyName || 'your workspace'} is active. Sign in with your email,
            your password and a code from your authenticator app.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: F1 - this invitation is finished (terminal)
  // ========================================================================
  // Placed AHEAD of every resume screen on purpose. This is the fix for the
  // 20 Aug incident: a consumed invitation was being headed "Continue your
  // setup" over a sign-in form that could never succeed, because the page had
  // no way to distinguish "parked, resume it" from "done, nothing to resume".
  // Nothing below may render for a consumed invitation, so the check goes
  // above all of them rather than being threaded through each.

  if (invitationConsumed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
            <CheckCircleIcon size={30} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Invitation already used</h2>
          <p className="text-gray-400 text-sm mb-6 leading-relaxed">
            This invitation has already been used to set up the account. Sign in at
            app.valutten.com.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: WS8b F2 - resume a parked setup
  // ========================================================================

  // R4 (V2-04): the challenge, rendered exactly as LoginPage does, with the
  // live resolver held in state (it cannot survive a reload).
  if (resumeMfaResolver) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <MfaChallengeCard
          resolver={resumeMfaResolver}
          onSuccess={() => void handleResumeChallengeSuccess()}
        />
      </div>
    );
  }

  // F-E (V3-06): the grant succeeded but the bootstrap session could not be
  // killed. Signposted, because a user who walks away leaves a refreshable
  // roleless session on an account that now holds full claims.
  if (revocationRetry) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
            <ShieldIcon size={30} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Almost there</h2>
          <p className="text-gray-400 text-sm mb-6 leading-relaxed">
            Your access is set up. We just need one more moment to secure this session.
          </p>
          <button
            type="button"
            onClick={() => {
              setRevocationRetry(false);
              void handleFinaliseEnrolment();
            }}
            disabled={finalising}
            className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {finalising ? 'Finishing setup...' : 'Finish setup'}
          </button>
        </div>
      </div>
    );
  }

  if (resumeRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
              <LockIcon size={30} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Continue your setup</h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              This invitation is already part-way through setup.{' '}
              {resumeFlow === 'acceptInvitation'
                ? 'Sign in with Google to finish turning on two-factor authentication.'
                : 'Sign in with the password you chose the first time to finish turning on two-factor authentication.'}
            </p>
          </div>

          {error && (
            <div
              className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg mb-6 flex items-start"
              role="alert"
            >
              <AlertCircleIcon className="mr-3 flex-shrink-0 mt-0.5" size={20} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* F-C (V3-03): a Google-bootstrapped account has no password. */}
          {resumeFlow === 'acceptInvitation' ? (
            <button
              type="button"
              onClick={() => void handleResumeGoogleSignIn()}
              disabled={resuming}
              className="w-full bg-white text-gray-900 p-3 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center font-medium"
            >
              {resuming ? (
                <>
                  <Spinner size="sm" className="mr-3" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Continue with Google</span>
                </>
              )}
            </button>
          ) : (
          <form onSubmit={handleResumeSignIn} className="space-y-5">
            <div>
              <label htmlFor="resume-email" className="block mb-2 text-sm font-medium text-gray-300">
                Email Address
              </label>
              <input
                type="email"
                id="resume-email"
                value={invitation?.email || ''}
                disabled
                className="w-full bg-gray-700/30 border border-gray-600 text-gray-400 text-sm rounded-lg px-3 py-3 cursor-not-allowed"
              />
            </div>
            <div>
              <label htmlFor="resume-password" className="block mb-2 text-sm font-medium text-gray-300">
                Password
              </label>
              <input
                type="password"
                id="resume-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
                disabled={resuming}
              />
            </div>
            <button
              type="submit"
              disabled={resuming || !password}
              className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {resuming ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  <span>Signing in&hellip;</span>
                </>
              ) : (
                <span>Sign in to continue setup</span>
              )}
            </button>
          </form>
          )}
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: WS8b - enrolment stage (roleless session, public route)
  // ========================================================================

  if (enrolmentInvitationId && user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4 py-12">
        {(enrolmentError || finalising) && (
          <div className="max-w-md w-full mb-4">
            {enrolmentError && (
              <div
                className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg flex items-start"
                role="alert"
              >
                <AlertCircleIcon className="mr-3 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-sm">{enrolmentError}</span>
              </div>
            )}
            {finalising && (
              <div className="flex items-center justify-center text-gray-400 text-sm mt-3">
                <Spinner size="sm" className="mr-2" />
                <span>Activating your access&hellip;</span>
              </div>
            )}
          </div>
        )}
        <MfaEnrollmentCard
          user={user}
          onEnrolled={handleFinaliseEnrolment}
          onReauth={() => {
            setInvitationAcceptanceMode(false);
            void auth.signOut().catch(() => {});
            navigate('/login', { replace: true });
          }}
          subtitle={`${invitation?.companyName || 'Your workspace'} requires a second step at sign-in. Set it up now - your access is activated the moment it is on.`}
        />
      </div>
    );
  }

  // ========================================================================
  // RENDER: Already Logged In
  // ========================================================================

  // CRITICAL: Use isAuthenticated (not user) to avoid race condition during Google OAuth
  // During invitation acceptance, user is set but claims is null, so isAuthenticated is false
  // This allows the form to remain visible while the invitation acceptance completes
  if (isAuthenticated && !showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <div className="max-w-md w-full">
          <div className="bg-yellow-900/50 border border-yellow-600 text-yellow-200 p-6 rounded-2xl text-center backdrop-blur-sm">
            <AlertCircleIcon className="mx-auto mb-4" size={48} />
            <h2 className="text-xl font-bold mb-2">Already Logged In</h2>
            <p className="mb-4">You are signed in as {user?.email}. To accept this invitation, please log out first.</p>
            {/* R2 (Codex SEC-8B-05): an authenticated reload lands HERE, not on
                the accepted screen, so without this the repair was unreachable in
                the one production state it was built for. Same handler, same
                refusal-code branching. */}
            {resumeFinishAction}
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all font-medium"
              >
                Go to Dashboard
              </button>
              <button
                onClick={logout}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all font-medium"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: Invalid Invitation
  // ========================================================================

  if (error && !invitation) {
    const isAlreadyUsed = error.toLowerCase().includes('already used');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <div className="max-w-md w-full">
          {isAlreadyUsed ? (
            <div className="bg-gray-800/50 border border-emerald-600 text-gray-200 p-6 rounded-2xl text-center backdrop-blur-sm">
              <CheckCircleIcon className="mx-auto mb-4 text-emerald-400" size={48} />
              <h2 className="text-xl font-bold mb-2 text-white">Account Already Created</h2>
              <p className="mb-4 text-gray-300">This invitation has already been accepted. You can sign in to access your dashboard.</p>
              {/* M10 (Codex SEC-8B-05): the reload route back into a setup that
                  granted access but never finished. Offered ONLY when an account
                  is signed in, because only a signed-in caller can be the bound
                  owner; the server re-checks that and refuses anyone else. R2
                  moved the markup into `resumeFinishAction` so this screen and
                  the "Already Logged In" screen cannot drift apart. */}
              {resumeFinishAction}
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all font-medium"
              >
                Sign In
              </button>
            </div>
          ) : (
            <div className="bg-red-900/50 border border-red-600 text-red-200 p-6 rounded-2xl text-center backdrop-blur-sm">
              <AlertCircleIcon className="mx-auto mb-4" size={48} />
              <h2 className="text-xl font-bold mb-2">Invalid Invitation</h2>
              <p className="mb-4">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all font-medium"
              >
                Go to Login
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: Success Screen
  // ========================================================================

  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-6 animate-pulse">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Welcome to {invitation?.companyName}!</h2>
            <p className="text-emerald-400 text-lg mb-6">Your account has been created successfully</p>
            <div className="flex justify-center">
              <Spinner size="lg" />
            </div>
            <p className="text-gray-400 text-sm mt-6">Redirecting to your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: Main Invitation Acceptance Form
  // ========================================================================

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-6 shadow-xl">
            <ClockIcon size={40} className="text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
            Accept Invitation
          </h1>
          <p className="text-lg text-gray-300">
            You've been invited to join{' '}
            <span className="text-emerald-400 font-semibold">{invitation?.companyName}</span>
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Invitation Details */}
          {invitation && (
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl shadow-xl p-8 h-fit">
              <h3 className="text-2xl font-semibold text-white mb-6">Invitation Details</h3>
              <div className="space-y-5">
                <div className="flex justify-between items-center pb-4 border-b border-gray-700">
                  <span className="text-gray-400 font-medium">Email:</span>
                  <span className="text-white font-semibold">{invitation.email}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-700">
                  <span className="text-gray-400 font-medium">Role:</span>
                  <span className="text-emerald-400 font-semibold capitalize">{invitation.role.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-700">
                  <span className="text-gray-400 font-medium">Invited by:</span>
                  <span className="text-white font-semibold">{invitation.invitedBy}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 font-medium">Expires:</span>
                  <span className="text-white font-semibold">{invitation.expiresAt.toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          )}

          {/* Right Column - Account Creation Form */}
          <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl border border-gray-700">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
                <LockIcon size={32} className="text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Create Your Account</h2>
              <p className="text-gray-400 text-sm">Enter your details to get started</p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg mb-6 flex items-start" role="alert">
                <AlertCircleIcon className="mr-3 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Single Unified Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Field (Read-only) */}
              <div>
                <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-300">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MailIcon size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={invitation?.email || ''}
                    disabled
                    className="w-full bg-gray-700/30 border border-gray-600 text-gray-400 text-sm rounded-lg pl-10 pr-3 py-3 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Display Name Field */}
              <div>
                <label htmlFor="displayName" className="block mb-2 text-sm font-medium text-gray-300">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    placeholder="Enter your full name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    autoComplete="name"
                    autoFocus
                    className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent pl-10 pr-3 py-3 transition-all duration-200"
                    required
                    minLength={2}
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-300">
                  Create Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent pr-12"
                    required
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    tabIndex={-1}
                    disabled={submitting}
                  >
                    {showPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirm-password" className="block mb-2 text-sm font-medium text-gray-300">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirm-password"
                    name="confirm-password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent pr-12"
                    required
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    tabIndex={-1}
                    disabled={submitting}
                  >
                    {showConfirmPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Password Strength Indicator */}
              {password && passwordStrength && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Password Strength</span>
                    <span className={`font-semibold ${
                      passwordStrength.label === 'Excellent' ? 'text-green-400' :
                      passwordStrength.label === 'Strong' ? 'text-green-500' :
                      passwordStrength.label === 'Good' ? 'text-yellow-400' :
                      passwordStrength.label === 'Fair' ? 'text-orange-400' :
                      'text-red-400'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${passwordStrength.color} transition-all duration-300`}
                      style={{ width: `${passwordStrength.percentage}%` }}
                    />
                  </div>

                  {/* Password Requirements Checklist */}
                  <div className="mt-4 space-y-2">
                    {passwordValidation && Object.entries(passwordValidation.requirements).map(([key, req]: [string, any]) => (
                      <div key={key} className="flex items-center gap-2 text-sm">
                        {req.met ? (
                          <CheckCircleIcon className="w-4 h-4 text-green-400" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-gray-600" />
                        )}
                        <span className={req.met ? 'text-green-400' : 'text-gray-400'}>
                          {req.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Password Match Indicator */}
              {confirmPassword && (
                <div className="flex items-center gap-2 text-sm">
                  {password === confirmPassword ? (
                    <>
                      <CheckCircleIcon className="w-4 h-4 text-green-400" />
                      <span className="text-green-400">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <AlertCircleIcon className="w-4 h-4 text-red-400" />
                      <span className="text-red-400">Passwords do not match</span>
                    </>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!passwordValidation?.allMet || password !== confirmPassword || submitting || !displayName.trim()}
                className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-800 flex items-center justify-center"
              >
                {submitting ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    <span>Creating your account...</span>
                  </>
                ) : (
                  <span>Accept Invitation & Create Account</span>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-gray-800/50 text-gray-400">Or continue with</span>
                </div>
              </div>
            </div>

            {/* Google Sign In */}
            <button
              onClick={handleGoogleSignIn}
              disabled={googleSignInActive || submitting}
              className="mt-6 w-full bg-white text-gray-900 p-3 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:ring-offset-gray-800 flex items-center justify-center font-medium"
            >
              {googleSignInActive ? (
                <>
                  <Spinner size="sm" className="mr-3" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Sign in with Google</span>
                </>
              )}
            </button>

            {/* Security Badge */}
            <div className="mt-8 pt-6 border-t border-gray-700">
              <div className="flex items-center justify-center text-xs text-gray-400">
                <ShieldIcon size={14} className="mr-2 text-green-400" />
                <span>Secured with Firebase Authentication & HTTPS</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
